import type { IChatMention } from "@/types/(my-space)/chat.interface";

/**
 * @mentions ("tags") in group and broadcast chats.
 *
 * A tag is the text "@<Full Name>" in the body plus the tagged user's id. The
 * server keeps a tag only while that exact text is in the body, so everything
 * here works on the text: the composer finds the "@query" at the caret and
 * swaps it for the picked name, the send recomputes which picked names are
 * still there, and the bubble splits the body back into text, links and tags.
 */

/** http(s) links, without the punctuation that usually ends a sentence. */
export const MESSAGE_URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

/** Typed after "@" before the suggestions give up. */
export const MENTION_QUERY_MAX_LENGTH = 50;
export const MENTION_SUGGESTIONS_LIMIT = 6;

interface PersonName {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * The name a tag is written with — the API's `fullName`: first and last name
 * joined by a space, trimmed. Never the localized fallback for a missing name.
 */
export const mentionName = (p: PersonName | null | undefined) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim();

/**
 * Case-, accent- and harakat-insensitive form for matching ("jose" finds
 * "José"; Arabic matches with or without its short vowels and hamza seats).
 */
export const foldForMatch = (text: string) =>
  text
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ًͯ-ٰٟ]/g, "");

// ─── Composer: the "@query" at the caret ───────────────────────────────────

export interface MentionQuery {
  /** Index of the "@". */
  start: number;
  /** What's typed between the "@" and the caret. */
  query: string;
}

/**
 * The tag being typed at `caret`: an "@" at the start or after whitespace,
 * with no line break between it and the caret. Spaces are allowed so a full
 * name can be typed ("@Sara Ah"); the suggestions close once nothing matches.
 */
export const findMentionQuery = (text: string, caret: number): MentionQuery | null => {
  const before = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const start = before.lastIndexOf("@");
  if (start < 0) return null;
  if (start > 0 && !/\s/.test(before[start - 1])) return null;
  const query = before.slice(start + 1);
  if (/[\r\n]/.test(query) || /^\s/.test(query)) return null;
  if (query.length > MENTION_QUERY_MAX_LENGTH) return null;
  return { start, query };
};

/**
 * Members who match `query` — a prefix of the first name, the last name or
 * the full name — without `excludeId` (me) and anyone with no name to write.
 * Sorted by name, at most `limit`.
 */
export const filterMentionCandidates = <T extends PersonName & { id: number }>(
  members: T[],
  query: string,
  excludeId: number | null | undefined,
  limit = MENTION_SUGGESTIONS_LIMIT,
): T[] => {
  const q = foldForMatch(query).trimStart();
  return members
    .filter((m) => m.id !== excludeId && mentionName(m))
    .filter((m) => {
      if (!q) return true;
      return [m.firstName ?? "", m.lastName ?? "", mentionName(m)].some((part) =>
        foldForMatch(part).startsWith(q),
      );
    })
    .sort((a, b) => mentionName(a).localeCompare(mentionName(b)))
    .slice(0, limit);
};

/**
 * Replaces the "@query" token (up to `caret`) with "@Full Name " and returns
 * the new text and where the caret goes. A space already after the caret is
 * reused rather than doubled.
 */
export const insertMention = (
  text: string,
  token: MentionQuery,
  caret: number,
  name: string,
): { text: string; caret: number } => {
  const before = text.slice(0, token.start);
  const after = text.slice(caret);
  const inserted = `@${name} `;
  return {
    text: before + inserted + (after.startsWith(" ") ? after.slice(1) : after),
    caret: before.length + inserted.length,
  };
};

// ─── Sending ───────────────────────────────────────────────────────────────

/**
 * The tags still in `body`: each picked person whose "@Full Name" text
 * survived the edits since, once per person, in body order. Read the way the
 * bubble reads it, so "@Ali Hassan" alone doesn't also tag a picked "Ali".
 */
export const mentionsInBody = (body: string, tagged: IChatMention[]): IChatMention[] => {
  const seen = new Set<number>();
  const out: IChatMention[] = [];
  splitMessageText(body, tagged).forEach((part) => {
    if (part.kind !== "mention" || seen.has(part.userId)) return;
    seen.add(part.userId);
    out.push({ userId: part.userId, name: part.name });
  });
  return out;
};

/** The `mentions` field to send: user ids, or undefined when there are none. */
export const mentionIdsToSend = (body: string, tagged: IChatMention[]) => {
  const ids = mentionsInBody(body, tagged).map((m) => m.userId);
  return ids.length ? ids : undefined;
};

// ─── Rendering ─────────────────────────────────────────────────────────────

export type MessageTextPart =
  | { kind: "text"; text: string }
  | { kind: "link"; text: string }
  | { kind: "mention"; text: string; userId: number; name: string };

const WORD_CHAR = /[\p{L}\p{N}_]/u;

/** One stretch of plain text (no links) split into text and tags. */
const splitMentions = (text: string, mentions: IChatMention[], out: MessageTextPart[]) => {
  // Longest first: "@Ali Hassan" is Ali Hassan's tag, not Ali's plus " Hassan".
  const byLength = [...mentions].sort((a, b) => b.name.length - a.name.length);
  let plain = "";
  let i = 0;
  while (i < text.length) {
    const atBoundary = i === 0 || !WORD_CHAR.test(text[i - 1]);
    const hit =
      text[i] === "@" && atBoundary
        ? byLength.find((m) => text.startsWith(m.name, i + 1))
        : undefined;
    if (!hit) {
      plain += text[i];
      i += 1;
      continue;
    }
    if (plain) out.push({ kind: "text", text: plain });
    plain = "";
    out.push({ kind: "mention", text: `@${hit.name}`, userId: hit.userId, name: hit.name });
    i += hit.name.length + 1;
  }
  if (plain) out.push({ kind: "text", text: plain });
};

/**
 * A message body as text, link and tag parts, in order; joined back they are
 * the body. Links win over tags (an "@" inside a URL stays part of it); a tag
 * needs its "@" at a word boundary, so an e-mail address is never one.
 */
export const splitMessageText = (
  text: string,
  mentions: IChatMention[] | null | undefined = [],
): MessageTextPart[] => {
  const usable = (mentions ?? []).filter((m) => m.name);
  const out: MessageTextPart[] = [];
  text.split(MESSAGE_URL_RE).forEach((segment, i) => {
    if (!segment) return;
    if (i % 2 === 1) out.push({ kind: "link", text: segment });
    else if (usable.length) splitMentions(segment, usable, out);
    else out.push({ kind: "text", text: segment });
  });
  return out;
};
